// player-panel — 53-я split-column orchestrator.
//
// Mounts .rh-root > .rh-songhead + .rh-pane > .rh-controls + .rh-waves, with
// transport-block + track rows in controls col, timeline band + wave rows in
// waves col. Attaches scroll sync between the two .rh-tracks-body siblings.
// Disables scroll sync at <768px breakpoint (mobile stack collapses both bodies).
//
// NOTE on mountTimeline: it builds its own .rh-waves > .rh-overview-band
// wrapper internally. We mount it into a dedicated slot inside our .rh-waves,
// so the .rh-overview-band is a descendant of .rh-pane > .rh-waves.
// Test selects .rh-overview-band anywhere in the tree — this passes.
//
// Key DOM contract:
//   host > .rh-root
//     > .rh-songhead                        (song header)
//     > .rh-pane
//       > .rh-controls
//           > .rh-transport-block           (transport)
//           > .rh-time-ruler-spacer
//           > .rh-tracks-head
//           > .rh-tracks-body > .tr-row × N
//       > .rh-waves
//           > .rh-timeline-slot             (mountTimeline host → builds .rh-overview-band)
//           > .rh-time-ruler
//           > .rh-tracks-head
//           > .rh-tracks-body > .tr-wave-row × N

import type {
  ShareManifest,
  SummaryPeaks,
  PerTrackSummaryPeaks,
} from '@bandway/share-types';
import { attachScrollSync } from '@bandway/ui-primitives';
import type { Player } from '../player.js';

import { mountSongHeader } from './song-header.js';
import { mountTransport } from './transport.js';
import { mountTimeline } from './timeline.js';
import { mountTrackRow, type TrackRowHandle } from './track-row.js';
import { mountTrackWaveRow, type TrackWaveRowHandle } from './track-wave-row.js';

const MOBILE_BREAKPOINT = '(max-width: 767px)';

export interface PlayerPanelHandle {
  unmount: () => void;
}

export function mountPlayerPanel(
  host: Element,
  manifest: ShareManifest,
  peaks: SummaryPeaks,
  player: Player,
  perTrackPeaks: PerTrackSummaryPeaks | null,
): PlayerPanelHandle {
  host.textContent = '';

  // ── DOM skeleton ───────────────────────────────────────────────────────
  const root = document.createElement('div');
  root.className = 'rh-root';

  // Song header slot (top of .rh-root, before .rh-pane)
  const songheadSlot = document.createElement('div');
  songheadSlot.className = 'rh-songhead';
  root.appendChild(songheadSlot);

  // .rh-pane-row wrapper — provides `flex:1; min-height:0` that gives the
  // grid pane explicit pixel height. Without this wrapper the grid's
  // implicit row stays content-sized and `.rh-controls/.rh-waves` internal
  // `grid-template-rows: 220px 38px 30px 1fr` collapses (tracks-body 1fr = 0px).
  // Mirrors desktop player-panel.js DOM (see waves-column.css `.rh-pane-row`).
  const paneRow = document.createElement('div');
  paneRow.className = 'rh-pane-row';

  const pane = document.createElement('div');
  pane.className = 'rh-pane';

  // ── Controls column ────────────────────────────────────────────────────
  const controls = document.createElement('div');
  controls.className = 'rh-controls';

  const transportBlock = document.createElement('div');
  transportBlock.className = 'rh-transport-block';
  controls.appendChild(transportBlock);

  const timeRulerSpacer = document.createElement('div');
  timeRulerSpacer.className = 'rh-time-ruler-spacer';
  controls.appendChild(timeRulerSpacer);

  const ctrlHead = document.createElement('div');
  ctrlHead.className = 'rh-tracks-head';
  controls.appendChild(ctrlHead);

  const ctrlBody = document.createElement('div');
  ctrlBody.className = 'rh-tracks-body';
  controls.appendChild(ctrlBody);

  // ── Waves column ───────────────────────────────────────────────────────
  // mountTimeline INTERNALLY builds `.rh-waves > .rh-overview-band > ...`.
  // Previously we wrapped it in our own outer `.rh-waves`, which caused
  // the nested `.rh-waves` to inherit desktop's `grid-column: 2` rule —
  // shifting the overview band into column 2 of an implicit 2-col grid
  // (resulting in ~50% empty space on the left of the waves area).
  //
  // Fix: let mountTimeline own `.rh-waves` directly as the column. Mount it
  // into a temp container first (because mountTimeline calls
  // `host.textContent = ''` — would wipe our .rh-controls if mounted into
  // .rh-pane directly), then move the produced `.rh-waves` into .rh-pane
  // alongside .rh-controls. After moving, append our time-ruler /
  // tracks-head / tracks-body — they auto-place into grid rows 2/3/4 since
  // timeline's overview-band has explicit `grid-row: 1`.
  const timelineSlot = document.createElement('div');
  paneRow.appendChild(pane);
  root.appendChild(paneRow);
  host.appendChild(root);

  // ── Mount song-header ─────────────────────────────────────────────────
  const songhead = mountSongHeader(songheadSlot, manifest);

  // ── Mount transport (in controls col) ─────────────────────────────────
  const transport = mountTransport(transportBlock, { player });

  // ── Mount timeline in temp slot, then move .rh-waves into .rh-pane ─────
  const timeline = mountTimeline(timelineSlot, manifest, peaks, player);
  const waves = timelineSlot.querySelector<HTMLElement>(':scope > .rh-waves');
  if (!waves) throw new Error('mountTimeline failed to create .rh-waves');

  // Order in .rh-pane: .rh-controls (grid-column: 1) + .rh-waves (grid-column: 2).
  // DOM append order doesn't matter for visual position (grid-column placement
  // wins) but we follow desktop convention controls-then-waves.
  pane.appendChild(controls);
  pane.appendChild(waves);

  // Append remaining waves-column children — auto-placed into grid rows 2/3/4.
  const timeRuler = document.createElement('div');
  timeRuler.className = 'rh-time-ruler';
  waves.appendChild(timeRuler);

  const wavesHead = document.createElement('div');
  wavesHead.className = 'rh-tracks-head';
  waves.appendChild(wavesHead);

  const wavesBody = document.createElement('div');
  wavesBody.className = 'rh-tracks-body';
  waves.appendChild(wavesBody);

  // ── Mount per-track rows ───────────────────────────────────────────────
  const ctrlHandles: TrackRowHandle[] = [];
  const waveHandles: TrackWaveRowHandle[] = [];

  manifest.tracks.forEach((t, i) => {
    ctrlHandles.push(mountTrackRow(ctrlBody, t, player, i));

    // PerTrackSummaryPeaks.tracks shape: Record<string, number[]>
    // Direct bins array per track id — no .bins wrapper.
    const bins = perTrackPeaks?.tracks?.[t.id] ?? null;
    waveHandles.push(mountTrackWaveRow(wavesBody, t, player, i, bins));
  });

  // ── Scroll sync ────────────────────────────────────────────────────────
  let detachScroll: (() => void) | null = null;

  function attachSync(): void {
    if (detachScroll) return;
    detachScroll = attachScrollSync(ctrlBody, wavesBody);
  }

  function detachSync(): void {
    if (!detachScroll) return;
    detachScroll();
    detachScroll = null;
  }

  // Guard: matchMedia may not be present in all test environments
  const mql =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(MOBILE_BREAKPOINT)
      : null;

  function onBreakpointChange(): void {
    if (mql?.matches) {
      detachSync();
    } else {
      attachSync();
    }
  }

  onBreakpointChange();
  mql?.addEventListener?.('change', onBreakpointChange);

  return {
    unmount: () => {
      detachSync();
      mql?.removeEventListener?.('change', onBreakpointChange);
      try { songhead.unmount(); } catch { /* best-effort */ }
      try { timeline.unmount(); } catch { /* best-effort */ }
      try { transport.unmount(); } catch { /* best-effort */ }
      for (const h of ctrlHandles) try { h.unmount(); } catch { /* best-effort */ }
      for (const h of waveHandles) try { h.unmount(); } catch { /* best-effort */ }
      host.textContent = '';
    },
  };
}
